import React, { useState, useEffect } from 'react';
import SimpleBar from 'simplebar-react';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import CountUp from "react-countup";
import FeatherIcon from "feather-icons-react";
import ReformUpcomingEvents from './ReformUpcomingEvents';
import PocketBaseStatus from './PocketBaseStatus';
import { useDashboardServices } from './pocketbaseServices';
import 'simplebar-react/dist/simplebar.min.css';

const DashboardProject = () => {
    // Tüm servisleri import ediyoruz
    const { getStatistics, getPocketBaseStatus, getRecentActivities } = useDashboardServices();
    const [dashboardData, setDashboardData] = useState({
        stats: {
            projects: { total: 0, active: 0, completed: 0, growth: 0 },
            invoices: { total: 0, pending: 0, totalRevenue: 0, monthlyRevenue: 0, growth: 0 },
            customers: { total: 0, active: 0, growth: 0 }
        },
        recentActivities: [],
        loginLogs: []
    });
    const [pbStatus, setPbStatus] = useState({ status: 'checking' });
    const [loading, setLoading] = useState(true);

    // Ana veri çekme useEffect'i
    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                const [stats, activities] = await Promise.all([
                    getStatistics(),
                    getRecentActivities() // Aktiviteleri çekiyoruz
                ]);

                setDashboardData(prev => ({
                    ...prev,
                    stats: stats || prev.stats,
                    recentActivities: activities || []
                }));
            } catch (error) {
                console.error("Dashboard veri çekme hatası:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
        const intervalId = setInterval(fetchAllData, 300000); // 5 dakikada bir yenile
        return () => clearInterval(intervalId);
    }, [getStatistics, getRecentActivities]);


    // PocketBase status çekme useEffect'i
    useEffect(() => {
        const fetchPocketBaseStatus = async () => {
            try {
                const status = await getPocketBaseStatus();
                setPbStatus(status);
            } catch (error) {
                console.error("PocketBase durumu alınamadı:", error);
                setPbStatus({ status: 'offline', message: 'Bağlantı sağlanamadı' });
            }
        };

        fetchPocketBaseStatus();
        const intervalId = setInterval(fetchPocketBaseStatus, 60000); // Her dakika kontrol et
        return () => clearInterval(intervalId);
    }, [getPocketBaseStatus]);

    // Manuel yenileme fonksiyonu
    const handleRefresh = async () => {
        try {
            const activities = await getRecentActivities();
            setDashboardData(prev => ({
                ...prev,
                recentActivities: activities || []
            }));
        } catch (error) {
            console.error("Aktiviteler yenilenirken hata:", error);
        }
    };

    const getDashboardCards = () => [
        {
            icon: "box",
            label: "Toplam Ürün",
            value: dashboardData.stats.products.total,
            percentage: dashboardData.stats.products.growth,
            color: "primary"
        },
        {
            icon: "file-text",
            label: "Teklifler",
            value: dashboardData.stats.invoices.total,
            percentage: dashboardData.stats.invoices.growth,
            color: "success"
        },
        {
            icon: "users",
            label: "Toplam Müşteri",
            value: dashboardData.stats.customers.total,
            percentage: dashboardData.stats.customers.growth,
            color: "info"
        },
        {
            icon: "user-check",
            label: "Kurumsal Müşteri",
            value: dashboardData.stats.customers.kurumsal,
            percentage: dashboardData.stats.customers.growth,
            color: "warning"
        }
    ];
    const StatisticTile = ({ icon, label, value, percentage, color }) => (
        <Card className="card-animate">
            <CardBody>
                <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                        <span className={`avatar-title rounded-circle fs-2 bg-${color}-subtle text-${color}`}>
                            <FeatherIcon icon={icon} className={`text-${color}`} />
                        </span>
                    </div>
                    <div className="flex-grow-1 ps-3">
                        <h6 className="text-uppercase text-muted fs-13 mb-1">{label}</h6>
                        <h4 className="fs-22 fw-semibold mb-0">
                            {typeof value === 'number' && (
                                <CountUp
                                    start={0}
                                    end={value}
                                    duration={2}
                                    separator=","
                                />
                            )}
                        </h4>
                    </div>
                    {percentage !== undefined && percentage !== 0 && (
                        <div className="flex-shrink-0 text-end">
                            <span className={`badge bg-${color}-subtle text-${color} fs-12`}>
                                <i className={`ri-arrow-${percentage >= 0 ? 'up' : 'down'}-line fs-13 align-middle me-1`}></i>
                                {Math.abs(percentage)}%
                            </span>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Dashboard" pageTitle="Reform Panel" />

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Yükleniyor...</span>
                            </div>
                        </div>
                    ) : (
                        <Row>
                            <Col xxl={9}>
                                <Row>
                                    {getDashboardCards().map((card, index) => (
                                        <Col xl={3} md={6} key={index}>
                                            <StatisticTile {...card} />
                                        </Col>
                                    ))}
                                </Row>

                                <Row className="mt-4">
                                    <Col xs={12}>
                                        <Card>
                                            <CardBody>
                                                <div className="d-flex align-items-center mb-3">
                                                    <h4 className="card-title mb-0">Son Sürüm Güncellemeleri</h4>
                                                    <div className="flex-grow-1 text-end">
                                                        <button
                                                            className="btn btn-sm btn-ghost-primary"
                                                            onClick={handleRefresh}
                                                        >
                                                            <i className="ri-refresh-line align-middle me-1"></i>
                                                            Yenile
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* SimpleBar ile scrollable alan oluştur */}
                                                <SimpleBar style={{ maxHeight: '655px' }}>
                                                    {dashboardData.recentActivities.length === 0 ? (
                                                        <div className="text-center py-4">
                                                            <div className="avatar-sm mx-auto mb-3">
                                                                <div className="avatar-title rounded-circle bg-soft-info text-info fs-5">
                                                                    <i className="ri-time-line"></i>
                                                                </div>
                                                            </div>
                                                            <p className="text-muted mb-0">Henüz sürüm güncellemesi bulunmuyor</p>
                                                        </div>
                                                    ) : (
                                                        <div className="activity-timeline px-3">
                                                            {dashboardData.recentActivities.map((activity, index) => (
                                                                <div key={activity.id || index} className="activity-item pb-3 mb-3 border-bottom">
                                                                    <div className="d-flex align-items-start">
                                                                        <div className="flex-shrink-0 me-3">
                                                                            <div className="avatar-xs">
                                                                                <span className={`avatar-title rounded-circle bg-soft-${activity.color} text-${activity.color} fs-5`}>
                                                                                    <i className={activity.icon}></i>
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-grow-1">
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <h5 className="fs-14 mb-1">
                                                                                    {activity.title}
                                                                                    {activity.version && (
                                                                                        <span className="badge bg-soft-primary text-primary ms-2">
                                                                                            v{activity.version}
                                                                                        </span>
                                                                                    )}
                                                                                    {activity.invoice_number && (
                                                                                        <span className="badge bg-soft-dark text-dark ms-2">
                                                                                            {activity.invoice_number}
                                                                                        </span>
                                                                                    )}
                                                                                </h5>
                                                                                <small className="text-muted">
                                                                                    {activity.created}
                                                                                </small>
                                                                            </div>
                                                                            <p className="text-muted mb-2">
                                                                                {activity.version_note || activity.description}
                                                                            </p>
                                                                            <div className="d-flex align-items-center">
                                                                                <small className="mb-0 text-muted">
                                                                                    <i className="ri-user-line me-1"></i>
                                                                                    {activity.user}
                                                                                </small>
                                                                                {activity.id && (
                                                                                    <Link
                                                                                        to={`/reform-invoices/detail/${activity.id}`}
                                                                                        className="btn btn-link btn-sm ms-auto"
                                                                                    >
                                                                                        Detay <i className="ri-arrow-right-line align-middle"></i>
                                                                                    </Link>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </SimpleBar>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>

                            <Col xxl={3}>
                                <Row>
                                    {/* PocketBase Status */}
                                    <Col xl={12} className="mb-4">
                                        <PocketBaseStatus status={pbStatus} />
                                    </Col>
                                </Row>
                                <Row>
                                    {/* Upcoming Events */}
                                    <Col xl={12}>
                                        <ReformUpcomingEvents />
                                    </Col>
                                </Row>
                            </Col>

                        </Row>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default DashboardProject;